/**
 * The Side Menu's density scale (CTX-242) — one source of truth for the values
 * that repeat across every surface of the menu: the product nav, the Settings
 * drill-in, the footer bar and the collapsed rail.
 *
 * Before this module each surface carried its own hand-typed paddings and text
 * sizes, so they drifted apart (a Settings row did not match a nav row, the
 * footer used a third size). Import from here instead of re-typing a value, so
 * a future density change lands everywhere at once.
 *
 * These are complete literal Tailwind class strings on purpose: Tailwind
 * extracts classes statically from source, so a composed or interpolated class
 * (`` `px-${n}` ``) would emit no CSS. Never build one of these by template
 * string at a call site.
 */

/**
 * Rail width. Expanded 224px (was 19rem/304px); collapsed 64px (was 5rem/80px).
 *
 * 224px leaves ~168px of label room, which clears the longest label
 * ("Datacenter Interconnect", ~150px at 13px) on a single line — so `LABEL_CLAMP`
 * rarely engages at this width. It is kept regardless: it means a long label can
 * never be cut to an ellipsis, and it keeps the rail's width independent of the
 * menu's longest label, so adding one later does not force a re-widen.
 *
 * 192px was tried first and read as too tight; the absolute floor is the longest
 * single *word* ("Notifications", ~85px).
 */
export const RAIL_WIDTH_EXPANDED = "w-56";
export const RAIL_WIDTH_COLLAPSED = "w-16";

/**
 * Mobile drawer width — the same value as the expanded rail so the two read as
 * one component. It was a hardcoded 264px, which tracked neither the rail it
 * mirrors (304px then) nor the new one, so the drawer and the desktop menu drifted
 * as one changed. At a 375px viewport 224px leaves roughly 40% of the screen as
 * backdrop, the normal drawer proportion.
 */
export const RAIL_WIDTH_MOBILE = RAIL_WIDTH_EXPANDED;

/**
 * A navigable row: menu items, Settings rows, Admin-center rows, footer rows.
 *
 * **13px label in a 28px row**, measured off the reference sidebar itself rather
 * than taken from the ticket's prose: its nav label computes to 13px Inter in a
 * 28px row, with the label inset 8px from the left. CTX-335's written spec said
 * 12.5px / 30px, which is what this module briefly carried; the live values win,
 * because the prose was describing that sidebar from memory.
 *
 * The two halves derive each other and neither is readable alone. A label carries
 * `leading-tight` (see `LABEL_CLAMP`), so its line box is 13 x 1.25 = 16.25px,
 * leaving 11.75px of padding to split — `py-1.5` (6px a side) lands 28.25px, the
 * 28px row to within a device pixel, and stays on the spacing scale. Change the
 * type size without re-deriving the padding and the row silently leaves 28px;
 * the density test pins the product of the two so that fails loudly instead.
 *
 * Horizontal stays symmetric at 8px. The reference measures 8px left / 9px right,
 * but that 1px is its own gutter before a trailing count — reproducing it here
 * would break the equal-inset rule `NAV_PADDING` documents for no visible gain.
 */
export const ROW_PADDING = "px-2 py-1.5";
export const ROW_TEXT = "text-[13px]";
export const ROW_RADIUS = "rounded-md";

/**
 * Leading icon box next to a label. 13px.
 *
 * CTX-242 set this to 10px and pinned it *under* `ROW_TEXT`, reasoning that the
 * comparison is ink rather than box: "a line-art glyph fills its box edge to
 * edge while 12px type only reaches ~8.5px of cap height". CTX-335 asked for
 * 15px, which would have made the glyph the heaviest mark in the row — the one
 * thing CTX-242 was protecting against. 13px is the settled middle, and the
 * reason it works is that CTX-242's premise does not hold for these assets:
 * they are **filled solid paths with inconsistent viewBoxes** (`Icon.svg` is
 * 20x20, `IconServer.svg` 12x11, `IconLogs.svg` 14x11), not edge-to-edge
 * line art, so each one renders visibly smaller than its box. At 10px several
 * of them read as specks.
 *
 * The invariant that survived is no longer "under the nominal type size" — the
 * icon and the label are both 13px now — but **within the label's rendered line
 * box** (16.25px, see `ROW_PADDING`). The glyph still sits inside the band the
 * word occupies, so the word stays the mark the eye catches. That is what the
 * density test pins.
 *
 * Held at 13px deliberately when the label moved 12.5px -> 13px: these assets
 * carry more internal padding than the reference sidebar's line art, so matching
 * its icon size by the number would render ours visibly smaller. The size is
 * pinned to how they *look*, not to what the spec sheet says.
 *
 * Every glyph sharing a row with a label is pinned here or to `CHEVRON_ICON`
 * (the same 13px), so no icon in the rail — leading or trailing — escapes that
 * line box. Label-less rows are the exception: see `ROW_ICON_STANDALONE`.
 */
export const ROW_ICON = "h-[13px] w-[13px]";

/**
 * Icon in a row that has *no* label: the collapsed rail, the collapsed footer
 * buttons, and the bordered chrome toggles (collapse / expand / close). 15px.
 *
 * Nothing competes with the glyph here, so the rule above does not apply — it is
 * the row's entire content. Held one step above `ROW_ICON` because at a 64px rail
 * (or inside a 28px bordered square) the glyph is carrying the whole affordance,
 * and there is no text for it to rank against.
 *
 * It moved 12px -> 15px only to stay above `ROW_ICON`'s own rise to 13px; the
 * ranking is the rule, the numbers follow it. 15px is also the figure CTX-335
 * specified for a nav glyph, and it is a better fit here than there: this is the
 * one place in the rail where a glyph genuinely has nothing to rank against.
 */
export const ROW_ICON_STANDALONE = "h-[15px] w-[15px]";

/** Gap between a row's icon and its label. */
export const ROW_GAP = "gap-2";

/**
 * Label overflow behaviour for a menu row.
 *
 * Long labels **wrap onto a second line** ("Datacenter" / "Interconnect")
 * instead of being cut with an ellipsis. Truncating made the rail's width a
 * function of its longest label; wrapping decouples the two, so the rail can be
 * narrowed (and a longer label added later) without forcing a re-widen. Note this
 * is why 224px is a free choice rather than a floor — see `RAIL_WIDTH_EXPANDED`.
 * Beyond two lines it still clamps, and `break-words`
 * only engages for a single word too long to fit — so the row grows by at most
 * one line.
 *
 * Applies to every label surface (product nav rows, Settings rows, Admin-center
 * rows) so one long label does not behave differently depending on where it is
 * rendered.
 */
export const LABEL_CLAMP = "line-clamp-2 break-words leading-tight";

/**
 * Badge sitting inline beside a row label: `BETA`, `NEW`, the numeric alerts
 * count. 9px text — a step under `ROW_ICON`, because a badge annotates the label
 * rather than competing with it.
 *
 * `leading-none` is the load-bearing part. Without it the badge inherited a
 * normal line-height and stood **18px** tall against a 15px label line, which
 * pushed a badged row to 34px while its plain neighbours stayed 31px — the badge
 * was setting the row height. Pinned to 13px (9px + `py-[2px]`) it sits inside
 * the text line and the row keeps one height whether it carries a badge or not.
 *
 * Geometry only. Each call site adds its own colour, and the shape stays here so
 * the three inline badges cannot drift apart again: before this constant the
 * `BETA` badge measured 18px and the numeric badge 11px **on the same row**,
 * because one had `leading-none` and the other did not.
 *
 * `translate-y-px` is an **optical** correction, not a layout one. `items-center`
 * centres the pill on the label's *line box*, which is geometrically correct and
 * still reads as too high: a menu label is lowercase-dominant ("Alerts",
 * "Reports"), so its visual mass is the x-height block, which sits lower than the
 * line box it lives in. Measured on the Alerts row: the pill's centre landed
 * ~1.5px above the label's x-height centre. A 1px nudge splits the difference
 * between aligning to the cap block and aligning to the x-height block, which is
 * where an all-caps chip beside sentence-case text looks centred.
 *
 * It is a transform on purpose — it shifts nothing in flow, so a badged row still
 * measures the same height as a plain one.
 */
export const BADGE_INLINE =
  "shrink-0 translate-y-px rounded px-1 py-[2px] text-[9px] font-bold uppercase leading-none";

/**
 * Collapsed rail: `BETA` / `NEW` become a **dot**, not a shrunken pill.
 *
 * The pill did not survive the icon shrink. At 7px text in a 23×12 box it sat
 * over a 12px glyph — nearly twice the icon's width, covering a third of it — so
 * the badge hid the thing it was annotating and its text was too small to read
 * anyway. A 6px dot is half the icon and reads as a marker on it.
 *
 * No information is lost: the word is already `aria-hidden` (the expanded rail
 * carries it), so the dot conveys exactly what the 7px text did.
 *
 * The numeric count keeps `BADGE_OVERLAY` instead — a count *is* the
 * information, so it stays legible rather than collapsing to a dot.
 */
export const BADGE_DOT = "h-1.5 w-1.5 rounded-full";

/**
 * Collapsed rail, numeric count only. Held at 12px — the icon's own height — and
 * deliberately *not* shrunk with the glyph: 8px is the floor for readable
 * numerals, so this one overlaps the icon rather than getting smaller.
 */
export const BADGE_OVERLAY =
  "flex h-3 min-w-3 items-center justify-center rounded-full px-0.5 text-[8px] font-bold leading-none";

/**
 * Vertical rhythm. Two gaps, and the distance between them is the point: rows
 * inside a section sit 2px apart while the sections break at 12px, so the eye
 * finds a group before it reads any label in it.
 *
 * CTX-335 narrowed the section break from CTX-242's 28px instead of removing it.
 * 28px was set on the argument that a large contrast is what makes groups
 * legible at 12px type — true when a group is several rows deep, but it broke
 * down at the top of the rail, where the first section is a single row
 * (`Overview`): the break did not separate two groups so much as strand that one
 * row in a band of whitespace between the product switcher and the next header.
 * CTX-1101 tightened it again to 12px, six row gaps: with the layer sections
 * (L1/L2/L3) the rail holds more, shorter groups, and 16px spread them apart.
 * Each header still sits in its own row-height box, so the break reads at
 * 12px — while keeping `Overview` attached to the chrome
 * above it.
 *
 * Do **not** close the remaining distance further. A section break only
 * *slightly* larger than a row gap reads as a misalignment rather than as a
 * break, which is the one outcome worse than either extreme — so keep the ratio
 * decisive (>=2x) or drop the contrast entirely, never land between.
 */
/**
 * Gap between sibling rows inside one section. **2px**, giving a 30px pitch on a
 * 28px row.
 *
 * The reference sidebar measures 1px here (a consistent 29px pitch), and this
 * briefly carried that. It read as too tight in our rail, and the reason is the
 * icons rather than the spacing: ours are **filled solid glyphs** where the
 * reference uses light line art, so an identical seam carries more ink on either
 * side of it and the rows crowd. Copying a measurement across two different icon
 * styles reproduces the number, not the effect.
 *
 * 2px is also what CTX-335's prose specified — the three sources agree once the
 * icon weight is accounted for.
 *
 * The gap stays far smaller than the 6px of padding *inside* each row, which is
 * the property that matters: neighbouring labels sit 14px apart while their hover
 * fills nearly touch, so a group reads as one block of rows rather than as a list
 * of separate buttons — which is what the 12px `SECTION_STACK` then breaks.
 */
export const ROW_STACK = "space-y-0.5";
/** Gap between sections — the large half of that contrast. */
export const SECTION_STACK = "space-y-3";

/**
 * Section group header ("Services", "Monitoring", "Personal", …).
 *
 * `SECTION_HEADER_ROW` keeps the disclosure chevron immediately after the label
 * instead of floating it to the far right edge: pushed right, the chevron
 * detaches from the word it discloses and the header reads as a full-width
 * navigable row rather than a quiet group label. The button still spans the full
 * width for the click target — only its content is left-grouped.
 */
/* 11px (CTX-335, from 10px). Still under `ROW_TEXT` — a group label must stay
   quieter than the rows it heads — but 10px sat below this font's legible floor
   for uppercase tracking-wide text. Weight and colour live at the call site in
   `MenuSection`. */
export const SECTION_HEADER_TEXT = "text-[11px]";
/**
 * The header sits in a **row-height box** (`py-1.5`, the same as `ROW_PADDING`)
 * and is followed by a single row gap — so it participates in the vertical
 * rhythm as one more row rather than as a bare line of text floating above the
 * group.
 *
 * This is measured, not styled by eye. Against the reference sidebar, two
 * independent gaps have to hold at once: last-row-to-next-header (45.5px) and
 * header-to-first-row (29.5px). A bare 16.5px text line with 4px under it — what
 * CTX-335's prose specified — lands 38.4px and 26.4px, missing both. Boxing the
 * header to row height and closing the gap under it to `ROW_STACK` lands 44.4px
 * and 29.4px, fitting both. (`ROW_STACK` has since gone 1px -> 2px on the icon-
 * weight argument in its own docblock; the header follows it, so the header keeps
 * sitting in the rhythm rather than drifting to a gap of its own.)
 *
 * That is why the "4px below" in the ticket is not what shipped: 4px is what
 * pushes the header away from the rows it heads, and the two-gap fit is the
 * evidence against it. The break *above* is `SECTION_STACK`'s, 12px since
 * CTX-1101.
 */
export const SECTION_HEADER_PADDING = "px-2 py-1.5 mb-0.5";
export const SECTION_HEADER_ROW = "flex w-full items-center gap-1";

/**
 * Padding of the scrollable nav container that holds the sections.
 *
 * Horizontal padding matches `CHROME_PADDING` (12px) so the rail has a single
 * left edge: the nav previously sat at 8px while the account/tenant cards above
 * it sat at 12px, which read as the content being pushed against the left wall
 * relative to the chrome. Equal on both sides, so a row's label inset and its
 * trailing badge inset are the same distance from their respective edges.
 *
 * Vertical is asymmetric on purpose (CTX-335): **no** top padding, 8px at the
 * bottom. The chrome above no longer ends in a divider, so the nav's top padding
 * stacked directly onto the chrome's own 12px — 29px of empty band originally,
 * which read as the nav having come loose from its heading. Zeroing the top hands
 * that seam entirely to `CHROME_PADDING`, leaving 12px; the reference sidebar
 * keeps it tight and spends its vertical budget on `SECTION_STACK` instead, which
 * is where the hierarchy actually comes from.
 *
 * The bottom keeps its 8px so the last row does not collide with the footer bar.
 */
export const NAV_PADDING = "px-3 pt-0 pb-2";

/**
 * Indent applied to a nested submenu (a parent's children).
 *
 * The indent alone is a weak cue at 12px type, so children also carry a vertical
 * guide line — the branch affordance the reference sidebar uses for its deepest
 * level. The `ml-3 + pl-3` split puts a child's icon at ~33px, which lands under
 * the parent's *label* (~30px) rather than under its icon, so the nesting reads
 * as "belongs to the row above" instead of as a second column.
 */
export const NESTED_INDENT =
  "mt-1 ml-3 border-l border-semantic-border-secondary pl-3";

/**
 * Chevron / disclosure affordance next to a collapsible row. Tracks `ROW_ICON`
 * (13px) for the same reason, so a row's leading icon and its trailing chevron
 * weigh the same and the label stays the heaviest mark between them.
 */
export const CHEVRON_ICON = "h-[13px] w-[13px]";

/**
 * Square hit area for an icon-only control: collapsed-rail footer buttons, the
 * expand / collapse toggle, the mobile close button. 32px, down from 40px —
 * matched to `CHROME_TRIGGER`, which the toggle sits beside, so neither looks
 * oversized next to the other. A density test holds the two equal.
 */
export const COLLAPSED_SQUARE = "h-8 w-8";

/**
 * Top-chrome controls: the account-card trigger and the tenant-card breadcrumb.
 *
 * These were left at `h-8` with `text-sm` by the first pass, which read as
 * oversized once the nav below dropped to 12px — the chrome is the first thing
 * in the rail, so a mismatch there is the most visible one. The label still
 * uses `ROW_TEXT`; the height is back at 32px because the brand mark beside it
 * has to be legible, and a 20px logo was not. `COLLAPSED_SQUARE` follows, since
 * the two sit in the same row.
 */
export const CHROME_TRIGGER = "h-8";

/**
 * Brand logo inside the account-card trigger. 32px, filling the trigger's
 * height: the mark was unreadable beside the product name at 20, and it is the
 * thing this row exists to identify.
 */
export const CHROME_LOGO = "h-8 w-8";

/**
 * The product name beside that logo. Smaller than `ROW_TEXT` on purpose: the
 * 32px mark takes width the label used to have, and the point of the row is to
 * name the product — a name cut to an ellipsis does not. At 12px the longest
 * one ships whole ("CTX Reflector V2"), so `truncate` stays a backstop rather
 * than the normal case.
 */
export const CHROME_PRODUCT_LABEL = "text-[12px]";

/** Tenant-card sub-line strip ("Partner id: …" / customer id). */
export const SUBLINE_TEXT = "text-[10px]";

/**
 * Height of that sub-line strip, pinned rather than left to font metrics.
 *
 * It used to be whatever `text-[10px]`'s line box plus `py-0.5` came to — 20px
 * with the webfont loaded. Two surfaces now depend on the number: the real card
 * and `SideMenuSkeleton`, which reserves the card's height so the nav does not
 * jump when the profile lands (CTX-398). An emergent height cannot be reserved
 * accurately, and it also shifted by a pixel or two between the fallback font
 * and ProximaNova — the same class of jump this ticket exists to remove.
 */
export const CHROME_SUBLINE_STRIP = "h-5";

/** Padding of the top chrome (account card + tenant card) and the footer bar. */
export const CHROME_PADDING = "px-3 py-3";

/**
 * The seam between the top chrome and the first nav row — overrides
 * `CHROME_PADDING`'s 12px bottom. **16px, unconditionally.**
 *
 * CTX-335 specified 4px, and this was briefly a pair of values chosen by whether
 * the tenant card rendered: 4px with the `Partner › Customer` breadcrumb present
 * (the spec's figure, on the reading that the breadcrumb did the separating) and
 * 16px without it, since a CUSTOMER tenant has no breadcrumb and the switcher
 * would otherwise sit flush against the first nav row.
 *
 * The conditional was the wrong model. 4px turned out to read as too tight *with*
 * the breadcrumb as well — the card is a bordered box, so if anything it needs
 * more room beside the nav, not less. The rule is simply that the chrome is one
 * group and the nav is another, and the break between groups is 16px whatever the
 * chrome happens to contain. It stays at 16px when `SECTION_STACK` went to 12px
 * (CTX-1101): the chrome is a bordered card, and it needs the larger seam.
 *
 * 16px also matches the reference sidebar, whose workspace switcher sits ~16px
 * above its first nav row (44px centre-to-centre against a 29px pitch).
 */
export const CHROME_SEAM_ABOVE_NAV = "pb-4";
export const CHROME_STACK = "gap-2";
/** Footer gutters match the nav and the chrome, so all three share a left edge. */
export const FOOTER_PADDING = "px-3 py-2";
export const FOOTER_STACK = "gap-1";

/**
 * Brand logo in the Settings drill-in header. `max-h-[80px]` let a tall asset
 * dominate the panel; 40px matches the rest of the chrome.
 */
export const DRILL_IN_LOGO = "max-h-10";

/**
 * Keyboard focus ring for every interactive surface in the rail (CTX-335 AC:
 * "visible keyboard focus").
 *
 * `focus-visible`, not `focus`: the rail is made almost entirely of buttons and
 * links, and on `focus` a ring would fire on every mouse click too — which is
 * why several of these controls carried a bare `focus:outline-none` with nothing
 * put back. That silenced the keyboard user to spare the mouse user; scoping to
 * `focus-visible` serves both, so the `outline-none` is safe here.
 *
 * The offset ring needs a colour to sit against or it renders the ring on top of
 * the row's own hover fill — hence `ring-offset-semantic-bg-primary`, the rail's
 * background. A surface on a different fill must override that half.
 */
export const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-semantic-border-brand-secondary focus-visible:ring-offset-1 focus-visible:ring-offset-semantic-bg-primary";

/**
 * Motion. Both constants exist so `prefers-reduced-motion` is honoured by
 * construction (CTX-335 AC) instead of per call site — the rail had ~20 hand-typed
 * `transition-colors` / `transition-transform`, and a reader cannot tell whether a
 * missing `motion-reduce:` was a decision or an oversight.
 *
 * Reduced motion means *no* transition here rather than a shortened one: these
 * animate hover fills and a chevron flip, so removing them costs nothing but the
 * animation itself. Anything conveying state by movement alone would need a
 * different treatment.
 */
export const TRANSITION_COLORS = "transition-colors motion-reduce:transition-none";
export const TRANSITION_TRANSFORM =
  "transition-transform motion-reduce:transition-none";

/**
 * Opening animation for the rail's overlay panels, so a list unfolds from the
 * control that opened it instead of appearing fully formed. Shared by the account
 * card and the tenant selector so the two cannot drift into slightly different
 * motions.
 *
 * These run once on mount — the panels are conditionally rendered, so there is
 * no exit animation to pair with them.
 *
 * `motion-reduce:animate-none` for the same reason as the transitions above: the
 * movement is decoration, and nothing here conveys state by motion alone.
 */
export const PANEL_OPEN_DOWN =
  "origin-top animate-dropdown-open motion-reduce:animate-none";

